import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { TextField, Button as AriaButton } from "react-aria-components";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { Search, Star, Archive, FolderInput, Plus, X } from "lucide-react";
import type { FacilityNode } from "@arc/contracts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { AuthSession } from "../../../auth/keycloak.js";
import { useFacilities } from "../useFacilities.js";
import { useFacilityFavourites } from "../useFacilityFavourites.js";
import {
  buildForest,
  searchTree,
  expandToMatches,
  excludeArchived,
  flattenVisible,
  toggle,
  canDrop,
  isWithin,
  type FacilityTreeNode,
} from "../facility-tree.js";
import { useFacilityAuthz } from "../useFacilityAuthz.js";
import { FacilityTree } from "../FacilityTree.js";
import { FacilityDetailPane } from "./FacilityDetailPane.js";
import { useFacilityNavParams } from "../drilldown/useFacilityNavParams.js";
import { CountPill } from "../CountPill.js";
import { MoveFacilityDrawer } from "../MoveFacilityDrawer.js";
import { MoveConfirmDialog } from "../MoveConfirmDialog.js";
import { FacilityDetailsDrawer } from "../FacilityDetailsDrawer.js";
import { DeleteFacilityDialog } from "../DeleteFacilityDialog.js";
import { SubtreeConfirmDialog } from "../SubtreeConfirmDialog.js";
import { useRenameFacility, useArchiveFacility, useUnarchiveFacility, useDuplicateFacility } from "../useFacilityMutations.js";
import { useShowError } from "../../../error/ErrorProvider.js";

type FilterMode = "all" | "favorites" | "archived";

/**
 * Facility selection — the app's primary navigation screen (ADR-004 / P6.1). A **master-detail** layout: the caller's
 * whole permitted tree (fetched once, derive-on-read) on the LEFT as a styled timeline tree with instant client-side
 * search; the RIGHT pane is reserved for the Manuals view (a later slice) and shows a placeholder for now. Selecting a
 * facility drives the right pane. The legacy gray-blue background comes from semantic tokens (so dark mode works).
 */
export function FacilitySelection({
  session,
}: {
  session: AuthSession | null;
}) {
  const { t } = useTranslation();
  const facilities = useFacilities(session);
  const rename = useRenameFacility(session);
  const archiveMut = useArchiveFacility(session);
  const unarchiveMut = useUnarchiveFacility(session);
  const duplicateMut = useDuplicateFacility(session);
  const showError = useShowError();
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null); // to restore focus after the clear button clears + unmounts
  const treePaneRef = useRef<HTMLDivElement>(null); // the scrollable left pane — scopes the reveal-scroll query
  // Expand "domino": id → entrance-delay (ms) for each row revealed by the latest expand (empty otherwise). A timer
  // clears it once the cascade finishes so a later re-render doesn't re-tag those rows.
  const [revealDelays, setRevealDelays] = useState<ReadonlyMap<number, number>>(EMPTY_DELAYS);
  const revealClearTimer = useRef<number | null>(null);
  // Ids whose collapse is mid-glide (the subtree removal is deferred behind a smooth scroll) — a repeat collapse for a
  // pending id is ignored, so it can't queue a second removal (see onToggleExpand: two toggles would cancel out).
  const collapsingIds = useRef<Set<number>>(new Set());
  // Clear the pending cascade-clear timer on unmount (it holds a setState that would otherwise fire after teardown).
  useEffect(() => () => {
    if (revealClearTimer.current !== null) clearTimeout(revealClearTimer.current);
  }, []);
  const [mode, setMode] = useState<FilterMode>("all");
  // Archive/Unarchive confirmation (ADR-006 §2) — the pending action, or null. Mounted-per-open keeps state fresh.
  const [confirm, setConfirm] = useState<{ kind: "archive" | "unarchive"; node: FacilityNode } | null>(null);
  // Hard-delete (ADR-006 §4) — the node whose delete dialog is open (fetches stats + type-to-confirm), or null.
  const [deleting, setDeleting] = useState<FacilityNode | null>(null);
  // Duplicate (ADR-008) — the node pending a duplicate confirm, or null.
  const [duplicating, setDuplicating] = useState<FacilityNode | null>(null);
  const [userExpanded, setUserExpanded] = useState<ReadonlySet<number>>(
    new Set(),
  );
  // Persisted favourites (ADR-034 plan panel #3): the same server-backed, optimistic hook as the drill-down — a star
  // set here reaches the Dashboard's Favourite facilities page and the Overview card (it was a local Set before).
  const favs = useFacilityFavourites(session);
  const favorites = favs.favourites;
  // The classic navigator keeps its selection in component state and writes no URL — but it CONSUMES `arc_facility`
  // on mount (ADR-032 plan panel): a printed QR label deep-links to a facility, and the workspace's close remounts this
  // view with the URL it had. Read once; a stale id simply selects nothing.
  const nav = useFacilityNavParams();
  const [selectedId, setSelectedId] = useState<number | null>(nav.manualsId);
  // Move (P6.2): the facility being moved + an optional pre-picked target (from a drop); the drag overlay's node; a
  // polite announcement for successful moves (the drag pointer path isn't the a11y path — the dialog is).
  // Move (P6.2 rework): the kebab "Move to…" opens the destination-PICKER drawer (source node); a DnD drop opens a
  // plain confirm ({source, target} — the drop already picked a valid parent).
  const [moveDrawerSource, setMoveDrawerSource] = useState<FacilityNode | null>(null);
  const [dropMove, setDropMove] = useState<{ source: FacilityNode; target: FacilityNode } | null>(null);
  const [activeNode, setActiveNode] = useState<FacilityNode | null>(null);
  const [announce, setAnnounce] = useState("");
  // Create: the parent an inline "new facility" card is open under (parentId null = a realm root), or null. The card
  // is rendered inside the tree (FacilityInlineCreate) rather than a drawer.
  const [createUnder, setCreateUnder] = useState<{ parentId: number | null } | null>(null);
  // Manage: the on-demand "Manage facility" drawer (categories etc.) for a facility — opened from that card's kebab
  // (panel: NOT auto-open on select, NOT an always-on column). Holds the facility id being managed (any node, not just
  // the selected one). Modal, so browsing pauses while it's open.
  const [managingId, setManagingId] = useState<number | null>(null);
  // Inline rename (ADR-006 §1): the row being renamed + its draft name as ONE atomic session — owned here so it
  // survives tree re-renders AND so a resolving save can only clear the session it started (panel: no cross-row
  // tear-down when saves and row-switches race).
  const [renameState, setRenameState] = useState<{ id: number; name: string } | null>(null);
  // Synchronous in-flight guard (a ref, not `isPending`, so it can't miss a same-tick double-submit before a
  // re-render lands — panel). Reset in onSettled.
  const renameSubmitting = useRef(false);
  // Same guard for Duplicate: the POST is ADDITIVE + non-idempotent (each call deep-copies the subtree), so a same-tick
  // double activation (Enter+click / key-repeat) before `isPending` publishes would create two copies. The confirm
  // button's `isDisabled={isPending}` is a render snapshot — insufficient on its own (panel).
  const duplicateSubmitting = useRef(false);

  const flat = useMemo(() => facilities.data ?? [], [facilities.data]);
  // A DRILL target (`arc_fac_selected` — e.g. a favourite ancestor the caller reaches only through descendants, ADR-034
  // diff panel): once the tree is here, expand its path so the subtree shows, and select it when it is selectable.
  // Read once, like `arc_facility`; a stale id does nothing.
  const drillTarget = useRef(nav.selectedId);
  useEffect(() => {
    const id = drillTarget.current;
    if (id === null || flat.length === 0) return;
    drillTarget.current = null;
    const byId = new Map(flat.map((f) => [f.id, f] as const));
    const node = byId.get(id);
    if (!node) return;
    const path: number[] = [];
    for (let cur: FacilityNode | undefined = node; cur; cur = cur.parentId === null ? undefined : byId.get(cur.parentId)) path.push(cur.id);
    setUserExpanded((s) => new Set([...s, ...path]));
    if (!node.archived && node.permission !== null) setSelectedId(node.id);
  }, [flat]);
  // Client authz gates — mirrors of the server's (ADR-004/005/006/008/009), extracted VERBATIM into a shared hook so the
  // classic and drill-down navigators gate identically off one source of truth (FN3a). Browse-only state stays here.
  const { bypass, movableIds, canAddIds, manageSubtreeIds, canDuplicateIds, canInsertAboveIds, archivedIds, archivedRootIds } =
    useFacilityAuthz(flat);
  // Active (non-archived) nodes for All/Favorites, with active descendants of hidden archived parents HOISTED so they
  // never vanish (panel #2). The Archived tab uses the full `flat`.
  const activeFlat = useMemo(() => excludeArchived(flat), [flat]);
  // Per-tab counts for the filter badges (mirror what each tab SHOWS): All/Favorites exclude archived, Archived is the
  // archived total. Derived from `flat`, so they update the moment an archive/unarchive refetches the tree.
  const favoritesCount = useMemo(() => {
    const active = new Set(activeFlat.map((f) => f.id));
    return [...favorites].filter((id) => active.has(id)).length;
  }, [activeFlat, favorites]);

  // A small movement (6px) starts a drag; a plain click still selects and the star/move buttons still fire.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const nodeById = (id: number | string) =>
    flat.find((f) => f.id === Number(id)) ?? null;

  const onDragStart = (e: DragStartEvent) =>
    setActiveNode(nodeById(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    const source = nodeById(e.active.id);
    const target = e.over ? nodeById(e.over.id) : null;
    setActiveNode(null);
    // Only open the confirm dialog for a client-valid drop; an invalid drop is a no-op (the row wouldn't highlight).
    if (source && target && canDrop(source, target, bypass))
      setDropMove({ source, target });
  };
  const trimmed = query.trim();
  // Working set per mode: All/Favorites navigate the ACTIVE (hoisted) tree; Archived navigates the full tree (so it can
  // show archived nodes + their active-ancestor context). Search + the forest are derived from the working set.
  const workingFlat = mode === "archived" ? flat : activeFlat;
  const forest = useMemo(() => buildForest(workingFlat), [workingFlat]);
  // id → tree node, so the expand cascade can flatten just the expanded node's subtree to order its revealed rows.
  const forestIndex = useMemo(() => {
    const m = new Map<number, FacilityTreeNode>();
    const walk = (ns: readonly FacilityTreeNode[]) => {
      for (const n of ns) {
        m.set(n.id, n);
        walk(n.children);
      }
    };
    walk(forest);
    return m;
  }, [forest]);
  const search = useMemo(() => searchTree(workingFlat, query), [workingFlat, query]);

  const { visibleIds, forceExpanded } = useMemo(() => {
    if (mode === "archived") {
      if (trimmed !== "") {
        // Search reveals matching ARCHIVED nodes (by name) + their ancestor context — auto-expanded to each match.
        // Intersect archived ids with search MATCHES (not the already-ancestor-expanded search.visibleIds) so the
        // archived match — and its Unarchive button — stays visible (panel).
        const matches = new Set([...archivedIds].filter((id) => search.matchIds.has(id)));
        const view = expandToMatches(flat, matches);
        return { visibleIds: view.visibleIds, forceExpanded: view.expandedIds };
      }
      // No search → COLLAPSED by default: every archived node is VISIBLE (so a root can be expanded to reveal its
      // subtree), but only the ACTIVE ancestors of the archived ROOTS are force-expanded — so the archived roots start
      // collapsed and the user controls their expansion (`userExpanded`). A real archived root (no active ancestor)
      // simply sits collapsed at the top.
      return {
        visibleIds: expandToMatches(flat, archivedIds).visibleIds,
        forceExpanded: expandToMatches(flat, archivedRootIds).expandedIds,
      };
    }
    if (trimmed !== "")
      return { visibleIds: search.visibleIds, forceExpanded: search.expandedIds };
    if (mode === "favorites") {
      const fav = expandToMatches(workingFlat, favorites);
      return { visibleIds: fav.visibleIds, forceExpanded: fav.expandedIds };
    }
    return {
      visibleIds: null as Set<number> | null,
      forceExpanded: null as Set<number> | null,
    };
  }, [mode, trimmed, search, favorites, workingFlat, flat, archivedIds, archivedRootIds]);

  const rows = useMemo(
    () =>
      flattenVisible(forest, {
        isExpanded: (id) =>
          userExpanded.has(id) || (forceExpanded?.has(id) ?? false),
        isVisible: visibleIds ? (id) => visibleIds.has(id) : undefined,
      }),
    [forest, userExpanded, visibleIds, forceExpanded],
  );

  const selectedFacility = useMemo(() => {
    // No detail pane in the Archived tab, and archived nodes are never selectable (panel #3): derive null so a stale
    // selection can't keep the pane populated after switching tabs or archiving the selected node.
    if (selectedId == null || mode === "archived") return null;
    const node = flat.find((f) => f.id === selectedId);
    // An ancestor the caller reaches only through a descendant (permission null) has no manuals view — its fetch would
    // 403 forever (the direct-view rule). Never a selection, so a scanned label of such a node lands on the list (panel).
    if (!node || node.archived || node.permission === null) return null;
    return {
      id: node.id,
      name: node.name,
      childCount: flat.filter((f) => f.parentId === selectedId).length,
      categoryCount: node.categoryCount ?? 0,
      manualCount: node.manualCount ?? 0,
      // Manage is manage/access_manage (or bypass) — same tier as create/move (ADR-006/007).
      canManage: bypass || node.permission === "manage" || node.permission === "access_manage",
      // Manual ops gate on `manuals_manage`+ (WIDER than canManage) — any non-view permission qualifies; the server is
      // authoritative for the state-dependent `confirm` nuance (ADR-012).
      canManageManuals: bypass || (node.permission !== null && node.permission !== "view"),
      // The `confirm` tier (a subset of manuals_manage) — reveals the publish/unpublish/clear-flag review transitions
      // (ADR-018), so a manuals_manage-only caller isn't shown a guaranteed-403 Confirm/Unmark/reopen button (panel).
      canConfirmManuals:
        bypass || node.permission === "confirm" || node.permission === "manage" || node.permission === "access_manage",
    };
    // `mode` IS a dependency (panel): the guard above returns null in the Archived tab, so the memo must recompute on
    // a tab switch — else it returns the stale `mode==='all'` value and the detail pane keeps rendering a now-hidden node.
  }, [flat, selectedId, bypass, mode]);

  // The facility whose details drawer is open (opened from any card's kebab, not tied to selection). `canManage` is the
  // facility-management gate (manage/access_manage/bypass — never the manuals boolean; ADR-016 panel #1).
  const managingFacility = useMemo(() => {
    if (managingId == null) return null;
    const node = flat.find((f) => f.id === managingId);
    if (!node) return null;
    return {
      id: node.id,
      name: node.name,
      childCount: flat.filter((f) => f.parentId === managingId).length,
      categoryCount: node.categoryCount ?? 0,
      permission: node.permission,
      canManage: bypass || node.permission === "manage" || node.permission === "access_manage",
    };
  }, [flat, managingId, bypass]);

  // Inline rename handlers. Start seeds the draft with the current name; save trims, no-ops on unchanged/empty, else
  // calls the rename mutation (errors → shared dialog); cancel clears. The tree owns which row shows the edit field.
  const onStartRename = (node: FacilityNode) => setRenameState({ id: node.id, name: node.name });
  const onCancelRename = () => setRenameState(null);
  const onSaveRename = () => {
    // One in-flight rename at a time — guard the save path against a double-submit (panel). The ref is synchronous, so
    // a same-tick second Enter is rejected even before React re-renders with `isPending`.
    if (!renameState || renameSubmitting.current) return;
    const { id, name: draft } = renameState;
    const name = draft.trim();
    const current = flat.find((f) => f.id === id);
    if (!current || name === "" || name === current.name) {
      setRenameState(null);
      return;
    }
    renameSubmitting.current = true;
    rename.mutate(
      { id, name },
      {
        // Clear ONLY the session we saved: if the user has since started renaming a different row, leave that draft
        // intact rather than tearing it down (panel: resolving save must not clobber a newer edit).
        onSuccess: () => setRenameState((prev) => (prev && prev.id === id ? null : prev)),
        onError: showError,
        onSettled: () => {
          renameSubmitting.current = false;
        },
      },
    );
  };
  const renamingId = renameState?.id ?? null;
  const editName = renameState?.name ?? "";

  // Archive / Unarchive (ADR-006 §2) — both go through a confirm dialog; the mutation runs on confirm. Errors (incl.
  // the unarchive guards' 409s) route to the shared error dialog. On a successful ARCHIVE that swallows the selected
  // node's subtree, clear the selection so the detail pane doesn't briefly show a now-archived node before the refetch.
  const confirmPending = archiveMut.isPending || unarchiveMut.isPending;
  const onConfirmLifecycle = () => {
    if (!confirm || confirmPending) return;
    const { kind, node } = confirm;
    const mut = kind === "archive" ? archiveMut : unarchiveMut;
    mut.mutate(node.id, {
      onSuccess: () => {
        setConfirm(null);
        if (kind === "archive" && selectedId != null) {
          const sel = flat.find((f) => f.id === selectedId);
          if (sel && isWithin(sel.path, node.path)) setSelectedId(null);
        }
      },
      onError: (err) => {
        setConfirm(null);
        showError(err);
      },
    });
  };

  // Duplicate (ADR-008) — a confirm dialog; the deep copy runs on confirm. Errors (403/404/409-archived) route to the
  // shared error dialog. On success the tree refetches with the new "<name> Copy" sibling.
  const onConfirmDuplicate = () => {
    // Synchronous ref guard (mirror rename) — rejects a same-tick second activation before `isPending` re-renders, so
    // the additive POST can't fire twice → never two copies (panel).
    if (!duplicating || duplicateSubmitting.current) return;
    duplicateSubmitting.current = true;
    const node = duplicating;
    duplicateMut.mutate(node.id, {
      onSuccess: () => {
        setAnnounce(t("facilitySelection.duplicatedName", { name: node.name }));
        setDuplicating(null);
      },
      onError: (err) => {
        setDuplicating(null);
        showError(err);
      },
      onSettled: () => {
        duplicateSubmitting.current = false;
      },
    });
  };

  // Switch filter tab. Entering the restore-only Archived tab tears down any in-flight edit affordances (inline
  // rename / add-root create) so only Unarchive renders there (panel); the selection is MASKED to the tree (below),
  // not cleared, so it survives a round-trip back to All.
  const changeMode = (m: FilterMode) => {
    if (m === "archived") {
      setRenameState(null);
      setCreateUnder(null);
    }
    setMode(m);
  };

  const matchIds = trimmed !== "" ? search.matchIds : EMPTY;
  // Expand = a top-to-bottom "domino" cascade: the newly-revealed child rows fade + slide in one after another, each
  // delayed by its DFS order under the expanded node (the flat sibling list can't height-animate a subtree, but only
  // the NEW rows mount on expand — existing rows keep their DOM — so a per-row entrance staggered by order reads as the
  // reveal cascading down). Reduced-motion skips it entirely (rows appear at once); collapse is instant. The reveal-
  // scroll (gently pull off-fold children into view) runs after the new rows commit.
  const onToggleExpand = (id: number) => {
    if (forceExpanded?.has(id)) return; // a force-expanded ancestor under a filter is not user-collapsible (panel finding)
    const willExpand = !userExpanded.has(id);
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const commit = () => setUserExpanded((s) => toggle(s, id));
    if (willExpand) {
      commit();
      if (!reduce) scheduleRevealCascade(id);
      // Defer the scroll past the commit render (the new rows don't exist yet); layout is final immediately, so
      // scrolling while the rows are still fading in still lands them correctly.
      requestAnimationFrame(() => queueRevealScroll(id, reduce));
      return;
    }
    // Collapse: removing a subtree shrinks the scroll content, and if we're scrolled past the point the shortened
    // content can fill, the browser clamps scrollTop instantly — the abrupt jump-up users saw when collapsing a node
    // they'd scrolled into. Pre-empt it: scroll up by EXACTLY the amount that would otherwise clamp (derived from the
    // subtree's rendered pixel height), smoothly, THEN remove the subtree once the scroll settles — so the shrink lands
    // with no clamp. When no clamp would occur (node visible / plenty of content remains below), collapse immediately.
    // Reduced-motion / missing row → collapse immediately. The removal is an idempotent DELETE (not a toggle): when it's
    // deferred behind the glide, a second collapse would otherwise queue a second toggle and the two would cancel out,
    // leaving the branch stuck open (panel) — so we also ignore a repeat collapse while one is already gliding.
    const removeExpanded = () =>
      setUserExpanded((s) => {
        if (!s.has(id)) return s;
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    const row = treePaneRef.current?.querySelector<HTMLElement>(`[data-facility-id="${id}"]`);
    if (reduce || !row) {
      removeExpanded();
      return;
    }
    if (collapsingIds.current.has(id)) return; // a deferred collapse for this id is already gliding — ignore the repeat
    const scroller = scrollParentOf(row);
    const lastDesc = lastVisibleDescendant(row);
    const removedHeight = lastDesc
      ? lastDesc.getBoundingClientRect().bottom - row.getBoundingClientRect().bottom
      : 0;
    const postCollapseMax = Math.max(0, scroller.scrollHeight - removedHeight - scroller.clientHeight);
    const target = Math.min(scroller.scrollTop, postCollapseMax);
    if (target >= scroller.scrollTop - 1) {
      removeExpanded(); // no upward correction needed → no clamp → collapse instantly
      return;
    }
    collapsingIds.current.add(id);
    scroller.scrollTo({ top: target, behavior: "smooth" });
    settleScroll(scroller, () => {
      collapsingIds.current.delete(id);
      removeExpanded(); // remove the subtree only after the glide settles (no snap)
    });
  };
  // Tag each newly-revealed row (the expanded node's visible descendants, in DFS order) with its entrance delay, so the
  // rows render with a staggered `animation-delay`. Cleared after the cascade so a later unrelated re-render doesn't
  // keep the stale tags. The clear timer is superseded by the next expand (a fresh cascade owns the tags).
  const scheduleRevealCascade = (id: number) => {
    const order = computeRevealDelays(id);
    setRevealDelays(order);
    if (revealClearTimer.current !== null) clearTimeout(revealClearTimer.current);
    const maxDelay = order.size ? Math.max(...order.values()) : 0;
    revealClearTimer.current = window.setTimeout(() => setRevealDelays(EMPTY_DELAYS), maxDelay + STAGGER_CLEAR_MS);
  };
  // End the cascade immediately, snapping every tagged row to its final (visible) state. Called when focus lands on a
  // still-animating row (keyboard nav faster than the cascade, or a click): the entrance holds a row at opacity 0 until
  // its delay elapses, so a focused-but-invisible row would be an a11y regression (focus not visible — panel). Clearing
  // the tags in one shot also avoids the flicker a per-row focus toggle would cause (re-adding `animate-in` on blur
  // replays the fade). Mouse expand focuses only the untagged parent, so the full domino still plays for pointer users.
  const cancelRevealCascade = () => {
    if (revealClearTimer.current !== null) clearTimeout(revealClearTimer.current);
    setRevealDelays((prev) => (prev.size ? EMPTY_DELAYS : prev));
  };
  // The expanded node's visible descendants (DFS order) → entrance delay in ms. Runs flattenVisible on just this node's
  // subtree with the post-expand predicate (so a descendant that was expanded while collapsed reveals its children too,
  // matching the real rows). The per-row delay is capped so a huge expansion still finishes promptly.
  const computeRevealDelays = (id: number): ReadonlyMap<number, number> => {
    const node = forestIndex.get(id);
    if (!node) return EMPTY_DELAYS;
    const isExpanded = (nid: number) => nid === id || userExpanded.has(nid) || (forceExpanded?.has(nid) ?? false);
    const isVisible = visibleIds ? (nid: number) => visibleIds.has(nid) : undefined;
    const sub = flattenVisible([node], { isExpanded, isVisible });
    const delays = new Map<number, number>();
    // skip index 0 (the expanded node itself, already on screen); its descendants cascade in.
    sub.slice(1).forEach((r, i) => delays.set(r.node.id, Math.min(i, STAGGER_MAX_STEPS) * STAGGER_STEP_MS));
    return delays;
  };
  // Scroll the LAST VISIBLE DESCENDANT of the just-expanded node into view, so off-fold children are revealed with
  // minimal movement (the parent stays visible unless the subtree exceeds the viewport). Resolve the target from the
  // RENDERED rows (DOM walk by aria-level) — never the pre-expand `flat` — so it can't target a hidden archived node
  // and it reaches deep already-expanded descendants (panel).
  const queueRevealScroll = (parentId: number, reduce: boolean) => {
    const parentRow = treePaneRef.current?.querySelector<HTMLElement>(`[data-facility-id="${parentId}"]`);
    if (!parentRow) return;
    lastVisibleDescendant(parentRow)?.scrollIntoView({ block: "nearest", behavior: reduce ? "auto" : "smooth" });
  };
  // The last RENDERED element of `parentRow`'s subtree — walk `nextElementSibling` while `aria-level` stays deeper. A
  // non-treeitem sibling is a subtree-owned inline-create card (a bare <div> with no aria-level; Fragments emit no DOM,
  // so it's the only kind here): COUNT it (set target) rather than skipping — else `removedHeight` undercounts on
  // collapse and the expand reveal-scroll leaves a trailing card off-fold (panel). Only a real treeitem back at/above
  // our level ends the subtree. Reading rendered rows (not the pre-toggle `flat`) reaches deep already-expanded
  // descendants and never targets a hidden archived node.
  const lastVisibleDescendant = (parentRow: HTMLElement): HTMLElement | null => {
    const parentLevel = Number(parentRow.getAttribute("aria-level") ?? "0");
    let target: HTMLElement | null = null;
    for (let el = parentRow.nextElementSibling; el instanceof HTMLElement; el = el.nextElementSibling) {
      if (!el.hasAttribute("aria-level")) {
        target = el; // a subtree-owned inline-create card — part of the range, not a boundary
        continue;
      }
      const level = Number(el.getAttribute("aria-level"));
      if (level <= parentLevel) break;
      target = el;
    }
    return target;
  };
  // The nearest scrollable ancestor of a row (the tree pane when it overflows, else the document) — the element whose
  // scrollTop the collapse glide animates, so we know when the glide has settled.
  const scrollParentOf = (el: HTMLElement): HTMLElement => {
    for (let p = el.parentElement; p; p = p.parentElement) {
      const oy = getComputedStyle(p).overflowY;
      if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight) return p;
    }
    return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
  };
  // Run `done` once the smooth glide finishes — poll the scroller's scrollTop until it stops changing (no reliable
  // cross-browser scroll-end event yet). If the node was already visible the glide is a no-op → settles in ~3 frames →
  // the collapse feels instant; a real glide settles when the animation lands. A frame cap (~1s) is the safety net.
  const settleScroll = (scroller: HTMLElement, done: () => void) => {
    let last = scroller.scrollTop;
    let stable = 0;
    let frames = 0;
    const poll = () => {
      const now = scroller.scrollTop;
      stable = Math.abs(now - last) < 0.5 ? stable + 1 : 0;
      last = now;
      frames += 1;
      if (stable >= 3 || frames > 60) {
        done();
        return;
      }
      requestAnimationFrame(poll);
    };
    requestAnimationFrame(poll);
  };
  const onToggleFavorite = (id: number) => favs.toggle(id);

  // Friendly SR announcements during a pointer drag (facility names, not ids). The dialog remains the primary a11y path.
  // onDragEnd mirrors the real handler's `canDrop` gate so an invalid drop is announced as rejected, not a false success
  // (panel finding). `over` is only set when the pointer is genuinely within a card (pointerWithin collision).
  const announcements = {
    onDragStart: ({ active }: { active: { id: number | string } }) =>
      t("facilitySelection.pickedUpName", { name: nodeById(active.id)?.name ?? t("facility.raw") }),
    onDragOver: ({
      active,
      over,
    }: {
      active: { id: number | string };
      over: { id: number | string } | null;
    }) =>
      over
        ? t("facilitySelection.isOverName", {
            source: nodeById(active.id)?.name ?? t("facility.Raw"),
            target: nodeById(over.id)?.name ?? t("facilitySelection.aFacility"),
          })
        : t("facilitySelection.noDropTarget"),
    onDragEnd: ({
      active,
      over,
    }: {
      active: { id: number | string };
      over: { id: number | string } | null;
    }) => {
      const s = nodeById(active.id);
      // NOT `t` — that is the translator in this scope, and shadowing it here is how a whole announcement block
      // silently stopped being translatable in an earlier slice.
      const dest = over ? nodeById(over.id) : null;
      if (s && dest && canDrop(s, dest, bypass)) return t("facilitySelection.droppedOn", { source: s.name, target: dest.name });
      return s && dest
        ? t("facilitySelection.cantMoveThere", { name: s.name })
        : t("facilitySelection.cancelledMoving", { name: s?.name ?? t("facility.raw") });
    },
    onDragCancel: ({ active }: { active: { id: number | string } }) =>
      t("facilitySelection.cancelledMoving", { name: nodeById(active.id)?.name ?? t("facility.raw") }),
  };

  return (
    <section
      aria-labelledby="facilities-heading"
      className="bg-muted flex h-full min-h-0 flex-col"
    >
      <div className="border-border bg-background border-b px-6 py-4">
        <h1 id="facilities-heading" className="text-xl font-semibold">
          {t("facilitySelection.facilitySelection")}
        </h1>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveNode(null)}
        accessibility={{ announcements }}
      >
        {/* Panes stack on narrow screens (tree over manuals) and sit side-by-side from `md` up (panel finding). */}
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {/* LEFT — the master timeline tree */}
          <div
            ref={treePaneRef}
            className="border-border flex w-full shrink-0 flex-col gap-3 overflow-y-auto border-b p-4 md:max-w-xl md:border-r md:border-b-0"
          >
            <TextField
              aria-label={t("facilitySelection.searchFacilities")}
              value={query}
              onChange={setQuery}
              className="relative w-full"
            >
              <Search
                aria-hidden="true"
                className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2"
              />
              <Input
                ref={searchRef}
                placeholder={t("facilitySelection.searchFacilities")}
                className="bg-background pr-8 pl-7"
              />
              {/* Clear the whole query in one click; only shown when there's text. Focus returns to the input so
                  keyboard users aren't dropped to <body> when the button unmounts. */}
              {query !== "" ? (
                <AriaButton
                  aria-label={t("facilitySelection.clearSearch")}
                  onPress={() => {
                    setQuery("");
                    searchRef.current?.focus();
                  }}
                  className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 absolute top-1/2 right-1.5 -translate-y-1/2 cursor-pointer rounded-sm p-0.5 outline-none focus-visible:ring-2"
                >
                  <X aria-hidden="true" className="size-4" />
                </AriaButton>
              ) : null}
            </TextField>

            <div
              className="flex items-center gap-1"
              role="group"
              aria-label={t("facilitySelection.filterFacilities")}
            >
              <Button
                variant={mode === "all" ? "secondary" : "ghost"}
                size="sm"
                aria-pressed={mode === "all"}
                onPress={() => changeMode("all")}
              >
                {t("facilitySelection.allFacilities")}
                <CountPill n={activeFlat.length} />
              </Button>
              <Button
                variant={mode === "favorites" ? "secondary" : "ghost"}
                size="sm"
                aria-pressed={mode === "favorites"}
                onPress={() => changeMode("favorites")}
              >
                <Star className="size-3.5" aria-hidden="true" />
                {t("facilitySelection.favorites")}
                <CountPill n={favoritesCount} />
              </Button>
              {/* Archived tab — shows archived subtrees (+ active-ancestor context); rows are restore-only. */}
              <Button
                variant={mode === "archived" ? "secondary" : "ghost"}
                size="sm"
                aria-pressed={mode === "archived"}
                onPress={() => changeMode("archived")}
              >
                <Archive className="size-3.5" aria-hidden="true" />
                {t("facilitySelection.archived")}
                <CountPill n={archivedIds.size} />
              </Button>
              {/* Add a ROOT facility — bypass-tier only (server enforces); hidden in the Archived tab (restore-only).
                Also shown when the realm is EMPTY so the first root is reachable (bypass is inferred from existing rows,
                which don't exist yet — panel). The real fix is a server-provided bypass-capability claim (ADR-005 Open
                Q #4); until then, empty-realm falls back to always-show and the server 403s a non-bypass caller. */}
              {mode !== "archived" && (bypass || flat.length === 0) ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  onPress={() => setCreateUnder({ parentId: null })}
                >
                  <Plus className="size-3.5" aria-hidden="true" />
                  {t("facilitySelection.addFacility")}
                </Button>
              ) : null}
            </div>

            {facilities.isPending ? (
              <p
                role="status"
                className="text-muted-foreground py-8 text-center text-sm"
              >
                {t("facilitySelection.loadingFacilities")}
              </p>
            ) : facilities.error ? (
              <p role="alert" className="py-8 text-center text-sm">
                {t("facilitySelection.couldNotLoadFacilities", { reason: facilities.error.message })}
              </p>
            ) : rows.length === 0 && !(createUnder && createUnder.parentId === null) ? (
              // Empty view — but NOT while a ROOT create is pending: the root inline-create card lives inside
              // FacilityTree, so an empty realm must still mount the tree to reach it (else the first-ever facility
              // can't be created — panel regression). parentId===null is the root create (don't invert this test).
              <p className="text-muted-foreground py-8 text-center text-sm">
                {flat.length === 0
                  ? t("facilitySelection.youDontHaveAccessToAny")
                  : trimmed !== ""
                    ? t("facilitySelection.noFacilitiesMatchYourSearch")
                    : mode === "favorites"
                      ? t("facilitySelection.noFavoritesYetTapTheStar")
                      : mode === "archived"
                        ? t("facilitySelection.noArchivedFacilities")
                        : t("facilitySelection.noFacilitiesMatchYourSearch")}
              </p>
            ) : (
              <FacilityTree
                rows={rows}
                matchIds={matchIds}
                revealDelays={revealDelays}
                onRevealedRowFocus={cancelRevealCascade}
                favorites={favorites}
                // Mask the selection in the Archived tab so no row shows selected (border/ring/aria-selected) in a
                // restore-only view — the selection survives for a return to All (panel).
                selectedId={mode === "archived" ? null : selectedId}
                movableIds={movableIds}
                bypass={bypass}
                canAddIds={canAddIds}
                manageSubtreeIds={manageSubtreeIds}
                canDuplicateIds={canDuplicateIds}
                canInsertAboveIds={canInsertAboveIds}
                archivedRootIds={archivedRootIds}
                archivedMode={mode === "archived"}
                renamingId={renamingId}
                editName={editName}
                savePending={rename.isPending}
                onEditNameChange={(v) => setRenameState((s) => (s ? { ...s, name: v } : s))}
                onToggleExpand={onToggleExpand}
                onToggleFavorite={onToggleFavorite}
                onSelect={setSelectedId}
                onMoveTo={(node) => setMoveDrawerSource(node)}
                onManage={(node) => setManagingId(node.id)}
                onArchive={(node) => setConfirm({ kind: "archive", node })}
                onDuplicate={(node) => setDuplicating(node)}
                onInsertAbove={(node) => {
                  // Insert above = a SIBLING of `node` = a child of its parent. Resolve the CANONICAL parent from `flat`
                  // (NOT `node.parentId`, which is HOISTED to the nearest active ancestor in All/Favorites — panel). The
                  // gate bars an archived/unmanageable/absent parent, so the canonical parent is active + rendered; the
                  // inline-create card mounts there and the new node lands as a true sibling.
                  const parentId = flat.find((f) => f.id === node.id)?.parentId ?? null;
                  if (parentId === null) return; // roots have no "insert above" (gate-barred); defensive
                  setCreateUnder({ parentId });
                  setUserExpanded((s) => {
                    const next = new Set(s);
                    next.add(parentId); // ensure the parent is open so the new sibling card is in view
                    return next;
                  });
                }}
                onDelete={(node) => setDeleting(node)}
                onUnarchive={(node) => setConfirm({ kind: "unarchive", node })}
                onStartRename={onStartRename}
                onSaveRename={onSaveRename}
                onCancelRename={onCancelRename}
                onAddChild={(node) => {
                  setCreateUnder({ parentId: node.id });
                  // Auto-expand the parent so its new inline child is in context (a collapsed parent would hide it).
                  setUserExpanded((s) => {
                    const next = new Set(s);
                    next.add(node.id);
                    return next;
                  });
                }}
                createUnder={createUnder}
                session={session}
                onCreated={(name) => {
                  setAnnounce(t("facilitySelection.createdName", { name }));
                  setCreateUnder(null);
                }}
                onCancelCreate={() => setCreateUnder(null)}
              />
            )}
          </div>

          {/* RIGHT — the selected facility's manuals (MV2 card grid). "Manage facility" lives on each card's kebab. */}
          <FacilityDetailPane facility={selectedFacility} session={session} />
        </div>

        {/* The floating card that follows the cursor while dragging — the "nice effect" (origin card dims in place). */}
        <DragOverlay dropAnimation={null}>
          {activeNode ? (
            <div className="pointer-events-none flex h-11 items-center gap-2 rounded-lg border border-primary bg-background px-3 shadow-lg ring-2 ring-primary/50">
              <FolderInput aria-hidden="true" className="text-primary size-4" />
              <span className="truncate text-sm font-semibold">
                {activeNode.name}
              </span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Polite announcement after a successful move (the drag itself is a pointer enhancement; the dialog is the a11y path). */}
      <p role="status" aria-live="polite" className="sr-only">
        {announce}
      </p>

      {/* Move destination PICKER drawer — opened from a card's kebab "Move to…" (fresh state each open). */}
      {moveDrawerSource ? (
        <MoveFacilityDrawer
          // Full `flat` + the parent's `bypass`: the drawer builds destination rows from the archived-EXCLUDED view
          // (no archived targets) but counts the real subtree (server moves `path <@`, incl. archived) + keeps the
          // source view's authorization tier (panel).
          source={moveDrawerSource}
          flat={flat}
          bypass={bypass}
          session={session}
          onClose={() => setMoveDrawerSource(null)}
          onMoved={(sourceName, destName) => setAnnounce(t("facilitySelection.movedInto", { source: sourceName, target: destName }))}
        />
      ) : null}

      {/* Drag-drop move confirm — a drop already picked a valid target, so this is a plain "are you sure?". */}
      {dropMove ? (
        <MoveConfirmDialog
          source={dropMove.source}
          target={dropMove.target}
          session={session}
          onClose={() => setDropMove(null)}
          onMoved={(sourceName, destName) => setAnnounce(t("facilitySelection.movedInto", { source: sourceName, target: destName }))}
        />
      ) : null}

      {/* Facility details drawer — opened from a card's kebab "Manage facility" (admin-only) → Manage mode. Modal, so
          browsing pauses while it's open; categories now, 3D later. */}
      {managingFacility ? (
        <FacilityDetailsDrawer
          facilityId={managingFacility.id}
          facilityName={managingFacility.name}
          childCount={managingFacility.childCount}
          categoryCount={managingFacility.categoryCount}
          permission={managingFacility.permission}
          canManage={managingFacility.canManage}
          canManageManuals={managingFacility.canManage}
          session={session}
          initialMode="manage"
          onClose={() => setManagingId(null)}
        />
      ) : null}

      {/* Archive / Unarchive confirmation — mounted only while pending (fresh state each open). The message names the
          subtree size (archive/unarchive cascade the whole subtree). Errors route to the shared error dialog. */}
      {confirm ? (
        <SubtreeConfirmDialog
          kind={confirm.kind}
          node={confirm.node}
          session={session}
          title={confirm.kind === "archive" ? t("facilitySelection.archiveFacility") : t("facilitySelection.restoreFacility")}
          confirmLabel={confirm.kind === "archive" ? t("facilitySelection.archive") : t("facilitySelection.restore")}
          isPending={confirmPending}
          onConfirm={onConfirmLifecycle}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {/* Hard-delete (ADR-006 §4, RED-ZONE endpoint) — mounted per-open; fetches stats + type-to-confirm. On success,
          clear the selection if the deleted subtree contained it (so the detail pane doesn't render a gone node before
          the tree refetch) and announce. */}
      {deleting ? (
        <DeleteFacilityDialog
          facility={{ id: deleting.id, name: deleting.name }}
          session={session}
          onClose={() => setDeleting(null)}
          onDeleted={(name) => {
            setAnnounce(t("facilitySelection.deletedName", { name }));
            if (selectedId != null) {
              const sel = flat.find((f) => f.id === selectedId);
              if (sel && isWithin(sel.path, deleting.path)) setSelectedId(null);
            }
          }}
        />
      ) : null}

      {/* Duplicate confirmation (ADR-008) — names the subtree size (the whole subtree is copied). Errors route to the
          shared error dialog; success refetches the tree with the new "<name> Copy" sibling. */}
      {duplicating ? (
        <SubtreeConfirmDialog
          kind="duplicate"
          node={duplicating}
          session={session}
          isPending={duplicateMut.isPending}
          onConfirm={onConfirmDuplicate}
          onCancel={() => setDuplicating(null)}
        />
      ) : null}
    </section>
  );
}

/** Stable empty set so the no-search case doesn't allocate a new Set each render. */
const EMPTY: ReadonlySet<number> = new Set();
/** Stable empty reveal-delay map (no cascade in progress). */
const EMPTY_DELAYS: ReadonlyMap<number, number> = new Map();

/** Expand-cascade ("domino") tuning: the delay between consecutive revealed rows (the cadence — big enough that the
 *  reveal visibly rolls top-to-bottom), a cap so a huge expansion still finishes promptly (rows past the cap share the
 *  max delay), and how long after the last row's delay to keep the tags before clearing them (must outlast the entrance
 *  animation — `--duration-base` ≈ 200ms — so no row is un-tagged mid-play). */
const STAGGER_STEP_MS = 34;
const STAGGER_MAX_STEPS = 16;
const STAGGER_CLEAR_MS = 500;
